import {
  Truck,
  Plus,
  Search,
  Edit2,
  Trash2,
  X,
  AlertCircle,
  CheckCircle,
  Loader2,
  Save,
  Filter,
  ChevronDown,
  Mail,
  Phone,
  MapPin,
  Star,
  StarOff,
  Building2,
  User,
  FileText,
  CreditCard,
  Wrench,
  Package,
  Cog,
  Leaf,
} from 'lucide-react';
import { useState, useEffect, useMemo } from 'react';

import { supabase } from '../../lib/supabase';
import { useAuth } from '../../providers/AuthProvider';

// ── Types ───────────────────────────────────────────────────────────────────

interface Supplier {
  id: string;
  organization_id: string;
  name: string;
  type: string;
  contact_name: string | null;
  email: string | null;
  phone: string | null;
  address: string | null;
  city: string | null;
  postal_code: string | null;
  tax_id: string | null;
  payment_terms: string | null;
  rating: number | null;
  is_active: boolean;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

interface SupplierFormData {
  name: string;
  type: string;
  contact_name: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  postal_code: string;
  tax_id: string;
  payment_terms: string;
  rating: number;
  notes: string;
}

// ── Constants ───────────────────────────────────────────────────────────────

const SUPPLIER_TYPES = [
  {
    value: 'service_provider',
    label: 'Usługodawca',
    icon: Wrench,
    color: 'text-blue-400',
    bg: 'bg-blue-500/20',
  },
  {
    value: 'material_supplier',
    label: 'Dostawca materiałów',
    icon: Package,
    color: 'text-green-400',
    bg: 'bg-green-500/20',
  },
  {
    value: 'equipment_supplier',
    label: 'Dostawca sprzętu',
    icon: Cog,
    color: 'text-orange-400',
    bg: 'bg-orange-500/20',
  },
  {
    value: 'ingredient_supplier',
    label: 'Dostawca ingredientów',
    icon: Leaf,
    color: 'text-emerald-400',
    bg: 'bg-emerald-500/20',
  },
];

const PAYMENT_TERMS_OPTIONS = [
  { value: '', label: 'Nie określono' },
  { value: 'prepaid', label: 'Przedpłata' },
  { value: 'net_7', label: '7 dni netto' },
  { value: 'net_14', label: '14 dni netto' },
  { value: 'net_30', label: '30 dni netto' },
  { value: 'net_60', label: '60 dni netto' },
  { value: 'net_90', label: '90 dni netto' },
  { value: 'cod', label: 'Za pobraniem (COD)' },
];

const initialFormData: SupplierFormData = {
  name: '',
  type: 'material_supplier',
  contact_name: '',
  email: '',
  phone: '',
  address: '',
  city: '',
  postal_code: '',
  tax_id: '',
  payment_terms: '',
  rating: 0,
  notes: '',
};

// ── Component ───────────────────────────────────────────────────────────────

export function AdminSuppliersPage() {
  const { currentOrganization, hasRole } = useAuth();

  // Data
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Search & filter
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<string>('all');
  const [showFilters, setShowFilters] = useState(false);

  // Modal
  const [showModal, setShowModal] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState<Supplier | null>(null);
  const [formData, setFormData] = useState<SupplierFormData>(initialFormData);
  const [isSaving, setIsSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Delete
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const canManage = hasRole(['owner', 'admin', 'manager']);

  // ── Fetch ─────────────────────────────────────────────────────────────

  useEffect(() => {
    if (currentOrganization) fetchSuppliers();
  }, [currentOrganization]);

  const fetchSuppliers = async () => {
    if (!currentOrganization) return;
    setIsLoading(true);
    try {
      const { data, error: err } = await supabase
        .from('suppliers')
        .select('*')
        .eq('organization_id', currentOrganization.organization_id)
        .order('name');
      if (err) throw err;
      setSuppliers(data || []);
    } catch (err) {
      console.error('Error fetching suppliers:', err);
      setError('Nie udało się załadować dostawców');
    } finally {
      setIsLoading(false);
    }
  };

  // ── Derived ───────────────────────────────────────────────────────────

  const filteredSuppliers = useMemo(() => {
    return suppliers.filter((s) => {
      const q = searchQuery.toLowerCase();
      const matchesSearch =
        !q ||
        s.name.toLowerCase().includes(q) ||
        s.contact_name?.toLowerCase().includes(q) ||
        s.email?.toLowerCase().includes(q) ||
        s.phone?.includes(q) ||
        s.city?.toLowerCase().includes(q) ||
        s.tax_id?.includes(q);
      const matchesType = filterType === 'all' || s.type === filterType;
      return matchesSearch && matchesType;
    });
  }, [suppliers, searchQuery, filterType]);

  const getTypeInfo = (type: string) =>
    SUPPLIER_TYPES.find((t) => t.value === type) || SUPPLIER_TYPES[0];

  // Helper available for future use in expanded detail views
  // const getPaymentLabel = (value: string | null) =>
  //   PAYMENT_TERMS_OPTIONS.find((o) => o.value === value)?.label || value || '-';

  // ── Handlers ──────────────────────────────────────────────────────────

  const openAddModal = () => {
    setEditingSupplier(null);
    setFormData(initialFormData);
    setFormError(null);
    setShowModal(true);
  };

  const openEditModal = (supplier: Supplier) => {
    setEditingSupplier(supplier);
    setFormData({
      name: supplier.name,
      type: supplier.type,
      contact_name: supplier.contact_name || '',
      email: supplier.email || '',
      phone: supplier.phone || '',
      address: supplier.address || '',
      city: supplier.city || '',
      postal_code: supplier.postal_code || '',
      tax_id: supplier.tax_id || '',
      payment_terms: supplier.payment_terms || '',
      rating: supplier.rating || 0,
      notes: supplier.notes || '',
    });
    setFormError(null);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingSupplier(null);
    setFormData(initialFormData);
    setFormError(null);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentOrganization) return;

    if (!formData.name.trim()) {
      setFormError('Nazwa dostawcy jest wymagana');
      return;
    }

    setIsSaving(true);
    setFormError(null);

    try {
      const payload = {
        organization_id: currentOrganization.organization_id,
        name: formData.name.trim(),
        type: formData.type,
        contact_name: formData.contact_name.trim() || null,
        email: formData.email.trim() || null,
        phone: formData.phone.trim() || null,
        address: formData.address.trim() || null,
        city: formData.city.trim() || null,
        postal_code: formData.postal_code.trim() || null,
        tax_id: formData.tax_id.trim() || null,
        payment_terms: formData.payment_terms || null,
        rating: formData.rating > 0 ? formData.rating : null,
        notes: formData.notes.trim() || null,
      };

      if (editingSupplier) {
        const { error: err } = await supabase
          .from('suppliers')
          .update(payload)
          .eq('id', editingSupplier.id);
        if (err) throw err;
        setSuccess('Dostawca został zaktualizowany');
      } else {
        const { error: err } = await supabase
          .from('suppliers')
          .insert([{ ...payload, is_active: true }]);
        if (err) throw err;
        setSuccess('Dostawca został dodany');
      }

      await fetchSuppliers();
      closeModal();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error saving supplier:', err);
      setFormError('Nie udało się zapisać dostawcy');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    setIsDeleting(true);
    try {
      const { error: err } = await supabase.from('suppliers').delete().eq('id', id);
      if (err) throw err;
      await fetchSuppliers();
      setDeleteTarget(null);
      setSuccess('Dostawca został usunięty');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error deleting supplier:', err);
      setError('Nie udało się usunąć dostawcy');
    } finally {
      setIsDeleting(false);
    }
  };

  // ── Rating renderer ───────────────────────────────────────────────────

  const RatingStars = ({ rating, size = 'sm' }: { rating: number; size?: 'sm' | 'md' }) => {
    const sizeClass = size === 'sm' ? 'w-3.5 h-3.5' : 'w-5 h-5';
    return (
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`${sizeClass} ${
              star <= rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-600'
            }`}
          />
        ))}
      </div>
    );
  };

  const RatingInput = ({ value, onChange }: { value: number; onChange: (v: number) => void }) => (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onClick={() => onChange(star === value ? 0 : star)}
          className="p-0.5 transition-colors"
        >
          <Star
            className={`w-6 h-6 ${
              star <= value
                ? 'text-yellow-400 fill-yellow-400'
                : 'text-gray-600 hover:text-gray-400'
            }`}
          />
        </button>
      ))}
      {value > 0 && (
        <button
          type="button"
          onClick={() => onChange(0)}
          className="ml-2 text-xs text-gray-500 hover:text-gray-300"
        >
          Wyczyść
        </button>
      )}
    </div>
  );

  // ── Loading ───────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="h-8 w-56 skel" />
            <div className="h-4 w-40 skel mt-2" />
          </div>
          <div className="h-10 w-40 skel" />
        </div>
        <div className="h-10 skel" />
        <div className="card tablecard">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="flex items-center gap-4 p-4 border-b border-aurora-border">
              <div className="h-10 w-10 skel" />
              <div className="flex-1 space-y-2">
                <div className="h-4 w-40 skel" />
                <div className="h-3 w-28 skel" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="pagehead">
        <div>
          <h1 className="pagehead__title flex items-center gap-2">
            <Truck className="w-6 h-6 text-amber-400" />
            Dostawcy
          </h1>
          <p className="pagehead__sub">
            Zarządzaj dostawcami organizacji (<span className="mono">{suppliers.length}</span>)
          </p>
        </div>
        {canManage && (
          <div className="pagehead__actions">
            <button onClick={openAddModal} className="btn btn--primary">
              <Plus className="w-5 h-5" />
              Dodaj dostawcę
            </button>
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {SUPPLIER_TYPES.map((type) => {
          const count = suppliers.filter((s) => s.type === type.value).length;
          const Icon = type.icon;
          return (
            <div key={type.value} className="statcard">
              <div className="statcard__top">
                <span className={`statcard__ico ${type.bg} ${type.color}`}>
                  <Icon />
                </span>
                <span className="statcard__lbl">{type.label}</span>
              </div>
              <div className="statcard__num">{count}</div>
            </div>
          );
        })}
      </div>

      {/* Messages */}
      {error && (
        <div className="flex items-center gap-2 p-4 bg-red-500/10 border border-red-500/30 rounded-ids-lg text-red-400">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{error}</span>
          <button onClick={() => setError(null)}>
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
      {success && (
        <div className="flex items-center gap-2 p-4 bg-green-500/10 border border-green-500/30 rounded-ids-lg text-green-400">
          <CheckCircle className="w-5 h-5 flex-shrink-0" />
          <span>{success}</span>
        </div>
      )}

      {/* Search & Filters */}
      <div className="space-y-3">
        <div className="flex gap-3">
          <div className="inputwrap flex-1">
            <Search />
            <input
              type="text"
              placeholder="Szukaj po nazwie, kontakcie, emailu, mieście, NIP..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="input"
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            aria-pressed={showFilters}
            className={`btn ${showFilters ? 'btn--tonal' : 'btn--secondary'}`}
          >
            <Filter className="w-4 h-4" />
            Filtry
            <ChevronDown
              className={`w-4 h-4 transition-transform ${showFilters ? 'rotate-180' : ''}`}
            />
          </button>
        </div>

        {showFilters && (
          <div className="card p-4">
            <div className="field sm:max-w-64">
              <label className="field__label">Typ dostawcy</label>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="select"
              >
                <option value="all">Wszystkie typy</option>
                {SUPPLIER_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Table */}
      {filteredSuppliers.length === 0 ? (
        <div className="card p-12 text-center">
          <Truck className="w-12 h-12 text-aurora-subtle mx-auto mb-4" />
          <h3 className="text-lg font-medium text-aurora-text mb-2">
            {searchQuery || filterType !== 'all' ? 'Nie znaleziono dostawców' : 'Brak dostawców'}
          </h3>
          <p className="text-aurora-muted mb-4">
            {searchQuery ? 'Spróbuj zmienić kryteria wyszukiwania' : 'Dodaj pierwszego dostawcę'}
          </p>
          {!searchQuery && canManage && (
            <button onClick={openAddModal} className="btn btn--primary">
              <Plus className="w-5 h-5" />
              Dodaj dostawcę
            </button>
          )}
        </div>
      ) : (
        <div className="card tablecard">
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>Nazwa</th>
                  <th>Typ</th>
                  <th>Kontakt</th>
                  <th>Email</th>
                  <th>Telefon</th>
                  <th>Miasto</th>
                  <th>Ocena</th>
                  {canManage && <th className="text-right">Akcje</th>}
                </tr>
              </thead>
              <tbody>
                {filteredSuppliers.map((supplier) => {
                  const typeInfo = getTypeInfo(supplier.type);
                  const TypeIcon = typeInfo.icon;
                  return (
                    <tr key={supplier.id}>
                      <td>
                        <div className="cellname">
                          <span
                            className={`w-8 h-8 rounded-ids-md grid place-items-center flex-shrink-0 ${typeInfo.bg}`}
                          >
                            <TypeIcon className={`w-4 h-4 ${typeInfo.color}`} />
                          </span>
                          <div>
                            <p className="text-sm">{supplier.name}</p>
                            {supplier.tax_id && (
                              <p className="text-xs font-normal text-aurora-subtle mono">
                                NIP: {supplier.tax_id}
                              </p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className={`badge ${typeInfo.bg} ${typeInfo.color}`}>
                          {typeInfo.label}
                        </span>
                      </td>
                      <td className="text-sm text-aurora-muted">{supplier.contact_name || '-'}</td>
                      <td>
                        {supplier.email ? (
                          <div className="flex items-center gap-1.5 text-sm text-aurora-muted">
                            <Mail className="w-3.5 h-3.5" />
                            <span className="truncate max-w-[150px]">{supplier.email}</span>
                          </div>
                        ) : (
                          <span className="text-sm text-aurora-subtle">-</span>
                        )}
                      </td>
                      <td>
                        {supplier.phone ? (
                          <div className="flex items-center gap-1.5 text-sm text-aurora-muted mono">
                            <Phone className="w-3.5 h-3.5" />
                            <span>{supplier.phone}</span>
                          </div>
                        ) : (
                          <span className="text-sm text-aurora-subtle">-</span>
                        )}
                      </td>
                      <td className="text-sm text-aurora-muted">{supplier.city || '-'}</td>
                      <td>
                        {supplier.rating && supplier.rating > 0 ? (
                          <RatingStars rating={supplier.rating} />
                        ) : (
                          <span className="text-sm text-aurora-subtle">
                            <StarOff className="w-4 h-4" />
                          </span>
                        )}
                      </td>
                      {canManage && (
                        <td>
                          <div className="flex items-center justify-end gap-1 rowact">
                            <button
                              onClick={() => openEditModal(supplier)}
                              className="p-1.5 text-aurora-muted hover:text-aurora-text hover:bg-aurora-surface rounded-ids-md transition-colors"
                              title="Edytuj"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => setDeleteTarget(supplier.id)}
                              className="p-1.5 text-aurora-muted hover:text-red-400 hover:bg-red-500/10 rounded-ids-md transition-colors"
                              title="Usuń"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60" onClick={() => setDeleteTarget(null)} />
          <div className="relative bg-aurora-surface border border-aurora-border rounded-ids-xl shadow-elev-4 max-w-md w-full">
            <div className="modal__head">
              <h3 className="modal__title">Potwierdzenie usuwania</h3>
            </div>
            <div className="modal__body">
              <p className="text-sm text-aurora-muted">
                Czy na pewno chcesz usunąć tego dostawcę? Tej operacji nie można cofnąć.
              </p>
            </div>
            <div className="modal__foot">
              <button onClick={() => setDeleteTarget(null)} className="btn btn--ghost">
                Anuluj
              </button>
              <button
                onClick={() => handleDelete(deleteTarget)}
                disabled={isDeleting}
                className="btn btn--danger"
              >
                {isDeleting && <Loader2 className="w-4 h-4 animate-spin" />}
                Usuń
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={closeModal} />
          <div className="relative bg-aurora-surface rounded-ids-xl shadow-elev-4 max-w-2xl w-full border border-aurora-border max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="modal__head">
              <div className="w-10 h-10 bg-amber-500/15 rounded-ids-md flex items-center justify-center flex-shrink-0">
                <Truck className="w-5 h-5 text-amber-400" />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="modal__title">
                  {editingSupplier ? 'Edytuj dostawcę' : 'Dodaj dostawcę'}
                </h2>
                <p className="text-sm text-aurora-muted truncate">
                  {editingSupplier ? editingSupplier.name : 'Wypełnij dane dostawcy'}
                </p>
              </div>
              <button onClick={closeModal} className="iconbtn">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto p-4 sm:p-6">
              <form id="supplier-form" onSubmit={handleSave} className="space-y-5">
                {formError && (
                  <div className="bg-red-500/10 border border-red-500/20 rounded-ids-lg p-4 flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-400">{formError}</p>
                  </div>
                )}

                {/* Name & Type */}
                <div>
                  <label className="block field__label mb-1.5">
                    <div className="flex items-center gap-1.5">
                      <Building2 className="w-4 h-4" />
                      Nazwa firmy <span className="text-aurora-brand">*</span>
                    </div>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    placeholder="np. ABC Dostawy Sp. z o.o."
                    className="input"
                  />
                </div>

                <div>
                  <label className="block field__label mb-1.5">Typ dostawcy</label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {SUPPLIER_TYPES.map((type) => {
                      const Icon = type.icon;
                      const isSelected = formData.type === type.value;
                      return (
                        <button
                          key={type.value}
                          type="button"
                          onClick={() => setFormData({ ...formData, type: type.value })}
                          aria-pressed={isSelected}
                          className={`flex items-center gap-2 p-3 rounded-ids-md border transition-all text-left ${
                            isSelected
                              ? 'border-aurora-brand bg-aurora-brand/10 shadow-glow'
                              : 'border-aurora-border hover:border-aurora-border-strong hover:bg-aurora-bg-subtle'
                          }`}
                        >
                          <div className={`p-1.5 rounded ${type.bg}`}>
                            <Icon className={`w-4 h-4 ${type.color}`} />
                          </div>
                          <span className="text-sm text-aurora-text font-medium">{type.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Contact info section */}
                <div className="pt-4 border-t border-aurora-border">
                  <h3 className="overline mb-4 flex items-center gap-2">
                    <User className="w-4 h-4 text-amber-400" />
                    Dane kontaktowe
                  </h3>

                  <div className="space-y-4">
                    <div>
                      <label className="block field__label mb-1.5">
                        <div className="flex items-center gap-1.5">
                          <User className="w-4 h-4" />
                          Osoba kontaktowa
                        </div>
                      </label>
                      <input
                        type="text"
                        value={formData.contact_name}
                        onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                        placeholder="Imię i nazwisko"
                        className="input"
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block field__label mb-1.5">
                          <div className="flex items-center gap-1.5">
                            <Mail className="w-4 h-4" />
                            Email
                          </div>
                        </label>
                        <input
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          placeholder="kontakt@firma.pl"
                          className="input"
                        />
                      </div>
                      <div>
                        <label className="block field__label mb-1.5">
                          <div className="flex items-center gap-1.5">
                            <Phone className="w-4 h-4" />
                            Telefon
                          </div>
                        </label>
                        <input
                          type="tel"
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          placeholder="+48 123 456 789"
                          className="input"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Address section */}
                <div className="pt-4 border-t border-aurora-border">
                  <h3 className="overline mb-4 flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-amber-400" />
                    Adres
                  </h3>

                  <div className="space-y-4">
                    <div>
                      <label className="block field__label mb-1.5">Adres</label>
                      <input
                        type="text"
                        value={formData.address}
                        onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                        placeholder="ul. Przykładowa 123"
                        className="input"
                      />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block field__label mb-1.5">Miasto</label>
                        <input
                          type="text"
                          value={formData.city}
                          onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                          placeholder="Warszawa"
                          className="input"
                        />
                      </div>
                      <div>
                        <label className="block field__label mb-1.5">Kod pocztowy</label>
                        <input
                          type="text"
                          value={formData.postal_code}
                          onChange={(e) =>
                            setFormData({ ...formData, postal_code: e.target.value })
                          }
                          placeholder="00-001"
                          className="input"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Business info section */}
                <div className="pt-4 border-t border-aurora-border">
                  <h3 className="overline mb-4 flex items-center gap-2">
                    <CreditCard className="w-4 h-4 text-amber-400" />
                    Dane rozliczeniowe
                  </h3>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block field__label mb-1.5">NIP</label>
                      <input
                        type="text"
                        value={formData.tax_id}
                        onChange={(e) => setFormData({ ...formData, tax_id: e.target.value })}
                        placeholder="123-456-78-90"
                        className="input mono"
                      />
                    </div>
                    <div>
                      <label className="block field__label mb-1.5">Warunki płatności</label>
                      <select
                        value={formData.payment_terms}
                        onChange={(e) =>
                          setFormData({ ...formData, payment_terms: e.target.value })
                        }
                        className="select"
                      >
                        {PAYMENT_TERMS_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Rating */}
                <div className="pt-4 border-t border-aurora-border">
                  <label className="block field__label mb-2">
                    <div className="flex items-center gap-1.5">
                      <Star className="w-4 h-4" />
                      Ocena dostawcy
                    </div>
                  </label>
                  <RatingInput
                    value={formData.rating}
                    onChange={(v) => setFormData({ ...formData, rating: v })}
                  />
                </div>

                {/* Notes */}
                <div>
                  <label className="block field__label mb-1.5">
                    <div className="flex items-center gap-1.5">
                      <FileText className="w-4 h-4" />
                      Notatki
                    </div>
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={3}
                    placeholder="Dodatkowe informacje o dostawcy..."
                    className="textarea resize-none"
                  />
                </div>
              </form>
            </div>

            {/* Footer */}
            <div className="flex flex-wrap items-center justify-between gap-3 px-4 sm:px-6 py-4 border-t border-aurora-border">
              <p className="text-xs text-aurora-subtle">
                <span className="text-aurora-brand">*</span> Pola wymagane
              </p>
              <div className="flex gap-3">
                <button type="button" onClick={closeModal} className="btn btn--secondary">
                  Anuluj
                </button>
                <button
                  type="submit"
                  form="supplier-form"
                  disabled={isSaving}
                  className="btn btn--primary min-w-[140px]"
                >
                  {isSaving ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  {editingSupplier ? 'Zapisz zmiany' : 'Dodaj dostawcę'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminSuppliersPage;
